import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import omit from 'lodash/omit';

import config from 'js/app/config';
import Retracked from 'js/app/retracked';
import type { PatchedLocationDescriptor } from 'js/lib/connectToRouter';
import { useLocation, useNavigate } from 'js/lib/useRouter';

import AuthenticationContent from 'bundles/authentication-common/components/authenticationModal/AuthenticationContent';
import Modal from 'bundles/authentication-common/components/modals/Modal';
import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication-common/constants';
import type { AuthModalContext } from 'bundles/authentication-common/types';
import { setLOHPSignupAbandonSignal } from 'bundles/authentication-common/utils/coachStorageUtils';
import { shouldShowProgressiveAuth } from 'bundles/authentication-common/utils/experimentUtils';
import { getAbsoluteRedirectToURL } from 'bundles/authentication-common/utils/navigationUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { AuthenticationContentBaseProps, AuthenticationModalBaseProps } from 'bundles/user-account/types';

import _t from 'i18n!nls/authentication';

export type PropsFromCaller = {
  /**
   * Use this prop to specify any additional variables, functions, etc. that a specific view in <AuthenticationContent>
   * might need
   */
  context?: AuthModalContext;
} & AuthenticationContentBaseProps &
  AuthenticationModalBaseProps;

const AuthenticationModal = (props: PropsFromCaller) => {
  const { defaultMode, isEnterprise, programId, onCloseRedirectURL, onClose, utilizeWebRedirectOnClose, ...restProps } =
    props;
  const location: PatchedLocationDescriptor = useLocation();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);

  const { redirectTo: defaultRedirectTo, context: contextFromProps } = props;
  const { query } = location;
  const {
    authProvider,
    verificationEmailRedirectUrl,
    authType,
    redirectTo: redirectToFromQuery,
    r,
    action,
    serviceCode,
    completeMode,
  } = query;
  const mode = defaultMode || location.query.authMode;

  useEffect(() => {
    const tracker = Retracked.makeTracker({
      namespace: 'open_course.user', // for backwards compatibility with userAuthorization
    });

    const mode = defaultMode || location.query.authMode;

    tracker(mode === AUTHENTICATION_MODAL_MODES.LOGIN ? 'login_modal.open' : 'signup_modal.open');

    const requestAnimationFrameId = requestAnimationFrame(() => {
      setIsOpen(true);
    });

    return () => cancelAnimationFrame(requestAnimationFrameId);
  }, [defaultMode, location.query, isEnterprise, programId]);

  const handleClose = useCallback<() => void>(() => {
    setIsOpen(false);
    setLOHPSignupAbandonSignal({ modalMode: mode });
    onClose?.();

    if (onCloseRedirectURL) {
      // I'm not using the redirect authentication action because I want the caller to be able to pass
      // in just a path and not a full url.
      if (utilizeWebRedirectOnClose) {
        window.location.replace(onCloseRedirectURL);
      } else {
        navigate({ pathname: onCloseRedirectURL });
      }
      return;
    }

    const currentPath = location.pathname;
    const query = omit(location.query, 'authMode');
    const searchParams = new URLSearchParams();

    // Add remaining query parameters
    Object.entries(query).forEach(([key, value]) => {
      if (value != null) {
        searchParams.set(key, String(value));
      }
    });

    navigate({
      pathname: currentPath,
      search: searchParams.toString() ? `?${searchParams.toString()}` : '',
    });
  }, [onClose, onCloseRedirectURL, utilizeWebRedirectOnClose, navigate, location.pathname, location.query, mode]);

  const currentURL = new URL(`${location.pathname}${location.search}${location.hash ?? ''}`, config.url.base);
  currentURL.searchParams.delete('authMode');
  const showProgressiveAuth = shouldShowProgressiveAuth({
    shouldDisableProgressiveAuth: !!isEnterprise || !!programId || !!authProvider,
  });

  const redirectTo = defaultRedirectTo || redirectToFromQuery || r;
  const redirectToURL = getAbsoluteRedirectToURL(redirectTo);
  const context = {
    action,
    accountType: serviceCode,
    authProvider,
    mode: completeMode,
    authMode: mode,
    service: authType,
    verificationEmailRedirectUrl,
    isEnterprise,
    showProgressiveAuth,
    ...contextFromProps,
  };

  const renderModal = () => {
    const commonProps = {
      ...restProps,
      contentLabel: _t('Authentication modal'),
      isOpen,
      onClose: handleClose,
    };
    const authContent = (
      <AuthenticationContent
        {...restProps}
        context={context}
        defaultMode={mode}
        redirectTo={redirectTo}
        programId={programId}
        magicLinkRedirect={redirectToURL || currentURL.toString()}
      />
    );

    return <Modal {...commonProps}>{authContent}</Modal>;
  };

  return (
    <TrackedDiv
      className="rc-AuthenticationModal"
      trackingName="authentication_modal"
      data-e2e="auth-modal"
      data-testid="auth-modal"
      trackClicks={false}
      withVisibilityTracking
    >
      {renderModal()}
    </TrackedDiv>
  );
};

export default Retracked.createTrackedContainer<PropsFromCaller>(() => ({
  namespace: {
    app: 'authentication',
  },
}))(AuthenticationModal);
